import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Star, Archive, Clock, Users, LayoutGrid } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import { useEzTask } from '../context/EzTaskContext';

const BOARD_COLORS = [
'#0079BF', '#D29034', '#519839', '#B04632',
'#89609E', '#CD5A91', '#4BBF6B', '#00AECC'];


export default function BoardList() {const { t } = useTranslation("eztask");
  const navigate = useNavigate();
  const { boards, loading, fetchBoards, createBoard } = useEzTask();
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [newBoard, setNewBoard] = useState({
    name: '',
    description: '',
    background_color: '#0079BF'
  });

  useEffect(() => {
    fetchBoards(showArchived);
  }, [fetchBoards, showArchived]);

  const handleCreateBoard = async () => {
    if (!newBoard.name.trim()) {
      toast.error('Board name is required');
      return;
    }
    try {
      const result = await createBoard(newBoard);
      toast.success('Board created successfully');
      setShowCreate(false);
      setNewBoard({ name: '', description: '', background_color: '#0079BF' });
      navigate(`/eztask/board/${result.board_id}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to create board');
    }
  };

  const starredBoards = boards.filter((b) => b.is_starred);
  const regularBoards = boards.filter((b) => !b.is_starred);

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="eztask-board-list">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <LayoutGrid className="w-8 h-8 text-blue-400" />{t("eztaskBoards", "EzTask Boards")}

            </h1>
            <p className="text-slate-400 mt-1">{t("manageYourProjects", "Manage your projects with Kanban boards")}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowArchived(!showArchived)}
              className={showArchived ? 'bg-slate-700' : ''}>
              
              <Archive className="w-4 h-4 mr-2" />
              {showArchived ? 'Show Active' : 'Show Archived'}
            </Button>
            <Button onClick={() => setShowCreate(true)} data-testid="create-board-btn">
              <Plus className="w-4 h-4 mr-2" />{t("createBoard", "Create Board")}

            </Button>
          </div>
        </div>

        {loading ?
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
          </div> :

        <>
            {/* Starred Boards */}
            {starredBoards.length > 0 &&
          <div className="mb-8">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />{t("starredBoards", "Starred Boards")}

            </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {starredBoards.map((board) =>
              <BoardCard key={board.id} board={board} onClick={() => navigate(`/eztask/board/${board.board_id}`)} />
              )}
                </div>
              </div>
          }

            {/* All Boards */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-slate-400" />
                {showArchived ? 'Archived Boards' : 'All Boards'}
              </h2>
              {regularBoards.length === 0 && starredBoards.length === 0 ?
            <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <LayoutGrid className="w-16 h-16 text-slate-600 mb-4" />
                    <p className="text-slate-400 text-lg mb-4">{t("noBoardsYet", "No boards yet")}</p>
                    <Button onClick={() => setShowCreate(true)}>
                      <Plus className="w-4 h-4 mr-2" />{t("createYourFirst", "Create your first board")}

                </Button>
                  </CardContent>
                </Card> :

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {regularBoards.map((board) =>
              <BoardCard key={board.id} board={board} onClick={() => navigate(`/eztask/board/${board.board_id}`)} />
              )}
                </div>
            }
            </div>
          </>
        }
      </div>

      {/* Create Board Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{t("createNewBoard", "Create New Board")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">{t("boardName", "Board Name *")}</label>
              <Input
                value={newBoard.name}
                onChange={(e) => setNewBoard((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter board name"
                className="bg-slate-900 border-slate-600"
                data-testid="board-name-input" />
              
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">{t("description", "Description")}</label>
              <Textarea
                value={newBoard.description}
                onChange={(e) => setNewBoard((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                className="bg-slate-900 border-slate-600"
                rows={3} />
              
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">{t("backgroundColor", "Background Color")}</label>
              <div className="flex gap-2 flex-wrap">
                {BOARD_COLORS.map((color) =>
                <button
                  key={color}
                  className={`w-10 h-10 rounded-lg transition-transform ${newBoard.background_color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800 scale-110' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setNewBoard((prev) => ({ ...prev, background_color: color }))} />

                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreateBoard} data-testid="create-board-submit">{t("createBoard", "Create Board")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

}

function BoardCard({ board, onClick }) {const { t } = useTranslation("eztask");
  return (
    <Card
      className="cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all overflow-hidden group"
      onClick={onClick}
      data-testid={`board-card-${board.board_id}`}>
      
      <div
        className="h-24 relative"
        style={{
          backgroundColor: board.background_color || '#0079BF',
          backgroundImage: board.background_image ? `url(${board.background_image})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        
        {board.is_starred &&
        <Star className="absolute top-2 right-2 w-4 h-4 text-yellow-400 fill-yellow-400" />
        }
      </div>
      <CardContent className="bg-slate-800 p-4">
        <h3 className="font-semibold text-white truncate">{board.name}</h3>
        <p className="text-sm text-slate-400 truncate mt-1">{board.description || 'No description'}</p>
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {board.member_count}
          </span>
          <span className="flex items-center gap-1">
            <LayoutGrid className="w-3 h-3" />
            {board.list_count}{t("lists", "lists")}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(board.created_at).toLocaleDateString()}
          </span>
        </div>
      </CardContent>
    </Card>);

}